import React, { useState, useEffect } from "react";
import { Trash2, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from "@/components/ui/form";
import { useForm } from "react-hook-form";
import { Switch } from "@/components/ui/switch";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/components/ui/use-toast";
import { TimeItem } from "./types";

interface TimesListProps {
  times: TimeItem[];
  onUpdateTime: (id: string, field: string, value: string | boolean | number) => void;
  onDeleteTime: (id: string) => void;
}

interface ZmanimData {
  shabbat_start?: string;
  shabbat_end?: string;
  sunrise?: string;
  sunset?: string;
  chatzot?: string;
  alot_hashachar?: string;
  tzeit_hakochavim?: string;
  [key: string]: string | undefined;
}

interface UserLocation {
  lat: number;
  lng: number;
  name: string;
  city?: string;
  country?: string;
  tzid?: string;
}

const RELATIVE_POINTS = [
  { value: "shabbat_start", label: "כניסת שבת" },
  { value: "shabbat_end", label: "יציאת שבת" },
  { value: "sunrise", label: "זריחה" },
  { value: "sunset", label: "שקיעה" },
  { value: "chatzot", label: "חצות היום" },
  { value: "alot_hashachar", label: "עלות השחר" },
  { value: "tzeit_hakochavim", label: "צאת הכוכבים" }
];

const TimesList: React.FC<TimesListProps> = ({ 
  times, 
  onUpdateTime, 
  onDeleteTime 
}) => {
  const [zmanimData, setZmanimData] = useState<ZmanimData>({});
  const [isLoadingZmanim, setIsLoadingZmanim] = useState(false);
  const [userLocation, setUserLocation] = useState<UserLocation>({
    lat: 32.4340, 
    lng: 34.9197, 
    name: "חדרה",
    city: "Hadera",
    country: "IL",
    tzid: "Asia/Jerusalem"
  });
  
  // Fetch user location from settings
  useEffect(() => {
    const fetchUserLocation = async () => {
      try {
        const { data, error } = await supabase
          .from('user_settings')
          .select('settings_value')
          .eq('settings_key', 'location')
          .single();
        
        if (error) {
          console.log("No user location found, using default Hadera coordinates:", error);
          return;
        }
        
        if (data?.settings_value) {
          const locationData = data.settings_value as { 
            geonameid?: string | number, 
            name?: string,
            city?: string,
            country?: string,
            tzid?: string
          };
          
          // Set the location data including city, country and timezone if available
          setUserLocation({
            lat: 32.4340, // Hadera coordinates
            lng: 34.9197,
            name: locationData.name || "חדרה",
            city: locationData.city || "Hadera",
            country: locationData.country || "IL",
            tzid: locationData.tzid || "Asia/Jerusalem"
          });
          
          if (locationData.geonameid) {
            // Fetch coordinates for the location
            const geoResponse = await fetch(
              `https://secure.geonames.org/getJSON?geonameId=${locationData.geonameid}&username=doravi`
            );
            
            if (geoResponse.ok) {
              const geoData = await geoResponse.json();
              setUserLocation(prevState => ({
                ...prevState,
                lat: parseFloat(geoData.lat),
                lng: parseFloat(geoData.lng)
              }));
              console.log("Using user location:", geoData.name, geoData.lat, geoData.lng);
            }
          }
        }
      } catch (error) {
        console.error("Error fetching user location:", error);
      }
    };
    
    fetchUserLocation();
  }, []);
  
  // Fetch zmanim data for relative time calculations
  useEffect(() => {
    const fetchZmanimData = async () => {
      // Check if we need to fetch zmanim (when we have relative times)
      const hasRelativeTimes = times.some(t => t.is_relative);
      
      if (!hasRelativeTimes) {
        return;
      }
      
      setIsLoadingZmanim(true);
      
      try {
        // First try to use the city/country method if available
        if (userLocation.city && userLocation.country) {
          console.log("Fetching shabbat times by city:", userLocation.city, userLocation.country);
          
          // Fetch shabbat times data from our new edge function with city/country
          const { data, error } = await supabase.functions.invoke('shabbat-times-by-city', {
            body: { 
              city: userLocation.city, 
              country: userLocation.country,
              tzid: userLocation.tzid || "Asia/Jerusalem"
            }
          });
          
          if (error) {
            console.error("Error from shabbat-times-by-city:", error);
            throw new Error(error.message);
          }
          
          if (data && data.shabbat && data.shabbat.length > 0) {
            console.log("Shabbat times data fetched by city:", data);
            
            // Convert the data format from Hebcal API format to our app's format
            const processedData: ZmanimData = {};
            
            // Process candles for shabbat_start
            const candlesItem = data.shabbat.find((item: any) => item.category === 'candles');
            if (candlesItem && candlesItem.title) {
              const match = candlesItem.title.match(/(\d{1,2}:\d{2})/);
              if (match && match[1]) {
                processedData.shabbat_start = match[1];
              }
            }
            
            // Process havdalah for shabbat_end
            const havdalahItem = data.shabbat.find((item: any) => item.category === 'havdalah');
            if (havdalahItem && havdalahItem.title) {
              const match = havdalahItem.title.match(/(\d{1,2}:\d{2})/);
              if (match && match[1]) {
                processedData.shabbat_end = match[1];
              }
            }
            
            // Fetch additional zmanim data from our original edge function for sunrise, sunset, etc.
            const today = new Date().toISOString().split('T')[0];
            const zmanimResponse = await supabase.functions.invoke('zmanim', {
              body: { 
                date: today, 
                lat: userLocation.lat, 
                lng: userLocation.lng,
                tzid: userLocation.tzid || "Asia/Jerusalem"
              }
            });
            
            if (!zmanimResponse.error && zmanimResponse.data) {
              // Merge zmanim data with shabbat times
              Object.assign(processedData, zmanimResponse.data);
            }
            
            setZmanimData(processedData);
            
            // Show location toast
            toast({
              title: "זמנים עודכנו",
              description: `הזמנים מחושבים לפי מיקום: ${userLocation.name || userLocation.city}`,
            });
            
            setIsLoadingZmanim(false);
            return;
          }
        }
        
        // Fallback to coordinates method
        console.log("Falling back to coordinates method for zmanim");
        const today = new Date().toISOString().split('T')[0];
        
        // Fetch shabbat times data from our edge function with user location
        const { data, error } = await supabase.functions.invoke('shabbat-times', {
          body: { 
            date: today, 
            lat: userLocation.lat, 
            lng: userLocation.lng,
            tzid: userLocation.tzid || "Asia/Jerusalem"
          }
        });
        
        if (error) {
          throw new Error(error.message);
        }
        
        if (data && data.times) {
          console.log("Shabbat times data fetched by coordinates:", data.times);
          setZmanimData(data.times);
          
          // Show location toast
          toast({
            title: "זמנים עודכנו",
            description: `הזמנים מחושבים לפי מיקום: ${userLocation.name}`,
          });
        } else {
          console.error("Invalid shabbat times data format:", data);
          toast({
            title: "שגיאת טעינה",
            description: "לא ניתן לטעון זמנים מדויקים",
            variant: "destructive"
          });
        }
      } catch (error) {
        console.error("Error fetching shabbat times data:", error);
        toast({
          title: "שגיאת טעינה",
          description: "אירעה שגיאה בטעינת הזמנים מהשרת",
          variant: "destructive"
        });
      } finally {
        setIsLoadingZmanim(false);
      }
    };
    
    fetchZmanimData();
  }, [times, userLocation]);

  // Function to extract hours and minutes from time string (HH:MM)
  const getHoursAndMinutes = (timeString: string) => {
    if (!timeString || !timeString.includes(':')) {
      return { hours: "00", minutes: "00" };
    }
    const [hours, minutes] = timeString.split(':');
    return {
      hours: hours.padStart(2, '0'),
      minutes: minutes.padStart(2, '0')
    };
  };

  // Function to handle hour change
  const handleHourChange = (id: string, hours: string) => {
    const timeObj = getHoursAndMinutes(times.find(t => t.id === id)?.time || '');
    const newTime = `${hours.padStart(2, '0')}:${timeObj.minutes}`;
    onUpdateTime(id, 'time', newTime);
  };

  // Function to handle minute change
  const handleMinuteChange = (id: string, minutes: string) => {
    const timeObj = getHoursAndMinutes(times.find(t => t.id === id)?.time || '');
    const newTime = `${timeObj.hours}:${minutes.padStart(2, '0')}`;
    onUpdateTime(id, 'time', newTime);
  };

  // Function to handle offset minutes change
  const handleOffsetChange = (id: string, offset: string) => {
    const offsetValue = parseInt(offset) || 0;
    onUpdateTime(id, 'offset_minutes', offsetValue);
  };
  
  // Function to calculate the actual time based on relative reference point
  const calculateRelativeTime = (relativePoint: string, offsetMinutes: number): string => {
    if (!relativePoint || !zmanimData[relativePoint]) {
      return "00:00";
    }

    try {
      const referenceTime = zmanimData[relativePoint] || "00:00";
      const [hours, minutes] = referenceTime.split(':').map(Number);
      
      const totalMinutes = hours * 60 + minutes + offsetMinutes;
      const newHours = Math.floor(totalMinutes / 60) % 24;
      const newMinutes = totalMinutes % 60;
      
      return `${newHours.toString().padStart(2, '0')}:${Math.abs(newMinutes).toString().padStart(2, '0')}`;
    } catch (error) {
      console.error("Error calculating relative time:", error);
      return "00:00";
    }
  };
  
  // Function to get the reference time for display in the UI
  const getReferenceTimeDisplay = (relativePoint: string): string => {
    if (!relativePoint || !zmanimData[relativePoint]) {
      return "(לא זמין)";
    }
    return zmanimData[relativePoint] || "(לא זמין)";
  };

  if (times.length === 0) {
    return (
      <div className="text-center p-6 bg-gray-50 rounded">
        <p className="text-gray-500">אין זמנים להצגה. הוסף זמן חדש להתחלה.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {userLocation && userLocation.name && (
        <div className="p-3 bg-blue-50 border border-blue-200 rounded-md text-sm text-blue-700">
          הזמנים מחושבים לפי מיקום: {userLocation.name}
        </div>
      )}
      
      {times.map((time) => {
        const { hours, minutes } = getHoursAndMinutes(time.time);
        const isRelative = time.is_relative || false;
        const showTime = time.show_time !== false;
        const calculatedTime = isRelative ? 
          calculateRelativeTime(time.relative_to || '', time.offset_minutes || 0) : 
          time.time;
        const referenceTimeDisplay = isRelative ? 
          getReferenceTimeDisplay(time.relative_to || '') : 
          '';
        
        return (
          <div key={time.id} className="flex flex-col gap-4 p-4 border rounded bg-white">
            <div className="flex flex-wrap gap-4">
              {/* Title Field */}
              <div className="flex-1 min-w-[200px]">
                <label className="block mb-2 text-sm">כותרת</label>
                <input
                  type="text"
                  value={time.title}
                  onChange={(e) => onUpdateTime(time.id, 'title', e.target.value)}
                  className="w-full p-2 border rounded"
                />
              </div>
              
              {/* Show Time Toggle */}
              <div className="w-40">
                <label className="block mb-2 text-sm">הצג זמן</label>
                <div className="flex items-center space-x-2">
                  <Switch
                    checked={showTime}
                    onCheckedChange={(checked) => onUpdateTime(time.id, 'show_time', checked)}
                  />
                  <span>{showTime ? 'כן' : 'לא'}</span>
                </div>
              </div>
              
              {/* Relative Time Toggle (only show if Show Time is enabled) */}
              {showTime && (
                <div className="w-40">
                  <label className="block mb-2 text-sm">סוג זמן</label>
                  <Select
                    value={isRelative ? "relative" : "absolute"}
                    onValueChange={(value) => onUpdateTime(time.id, 'is_relative', value === "relative")}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="absolute">זמן מדויק</SelectItem>
                      <SelectItem value="relative">זמן יחסי</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
              
              {/* Delete Button */}
              <div className="flex items-start">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => onDeleteTime(time.id)}
                  className="text-red-500 hover:text-red-700 hover:bg-red-50"
                >
                  <Trash2 size={18} />
                </Button>
              </div>
            </div>
            
            {/* Conditional rendering based on show_time and relative/absolute */}
            {showTime && (
              <div className="mt-2">
                {isRelative ? (
                  <div className="flex flex-wrap gap-4">
                    {/* Reference Point Dropdown */}
                    <div className="flex-1 min-w-[200px]">
                      <label className="block mb-2 text-sm">נקודת ייחוס</label>
                      <Select
                        value={time.relative_to || ""}
                        onValueChange={(value) => onUpdateTime(time.id, 'relative_to', value)}
                      >
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder="בחר נקודת ייחוס" />
                        </SelectTrigger>
                        <SelectContent>
                          {RELATIVE_POINTS.map(point => (
                            <SelectItem key={point.value} value={point.value}>
                              {point.label} {zmanimData[point.value] ? `(${zmanimData[point.value]})` : ''}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {isLoadingZmanim && (
                        <p className="text-xs text-gray-500 mt-1">טוען זמנים...</p>
                      )}
                      {referenceTimeDisplay && !isLoadingZmanim && (
                        <p className="text-xs text-gray-500 mt-1">
                          זמן ייחוס: {referenceTimeDisplay}
                        </p>
                      )}
                      {time.relative_to && calculatedTime && (
                        <p className="text-xs text-blue-600 font-medium mt-1">
                          זמן מחושב: {calculatedTime}
                        </p>
                      )}
                    </div>
                    
                    {/* Offset Minutes */}
                    <div className="w-40">
                      <label className="block mb-2 text-sm">דקות קיזוז</label>
                      <div className="flex items-center space-x-2">
                        <input
                          type="number"
                          value={time.offset_minutes || 0}
                          onChange={(e) => handleOffsetChange(time.id, e.target.value)}
                          className="w-full p-2 border rounded"
                        />
                        <span>דק'</span>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        (חיובי = אחרי, שלילי = לפני)
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="w-40">
                    <label className="block mb-2 text-sm">שעה (24 שעות)</label>
                    <div className="flex items-center">
                      <select 
                        value={hours}
                        onChange={(e) => handleHourChange(time.id, e.target.value)}
                        className="p-2 border rounded w-16"
                      >
                        {Array.from({ length: 24 }, (_, i) => i.toString().padStart(2, '0')).map(hour => (
                          <option key={hour} value={hour}>{hour}</option>
                        ))}
                      </select>
                      <span className="mx-1 font-bold">:</span>
                      <select 
                        value={minutes}
                        onChange={(e) => handleMinuteChange(time.id, e.target.value)}
                        className="p-2 border rounded w-16"
                      >
                        {Array.from({ length: 60 }, (_, i) => i.toString().padStart(2, '0')).map(minute => (
                          <option key={minute} value={minute}>{minute}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default TimesList;
